// src/components/batches/BatchTable.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/currency';
import { parseSessionDays } from '../../utils/sessionDays';
import { ListLoadingSpinner } from '../layout/ListPageLayout';
import { batchPolicyTitle } from '../../utils/carryForwardCopy';
import {
    formatChargesMode,
    isPeriodBillingMode,
    formatPeriodInterval,
} from '../../utils/periodBillingCopy';

const withHash = (name, id) => {
    const nm = name ?? '';
    const ident = id ?? null;
    return nm ? `${nm}${ident != null ? ` (#${ident})` : ''}` : (ident != null ? `#${ident}` : '—');
};

const BatchTable = ({ rows = [], loading = false, loadError = false, onEdit, onSessions, onArchive }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (loading) {
        return <ListLoadingSpinner />;
    }

    if (loadError) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                {t('common.listLoadFailed')}
            </div>
        );
    }

    const defaultGotoSessions = (b) => {
        const id = b.batchId ?? b.id;
        navigate(`/manager/sessions?batchId=${id}`);
    };

    if (!rows || rows.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-lg font-medium">{t('batches.empty')}</p>
                <p className="text-sm mt-1">{t('batches.emptyHint')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {rows.map((b) => {
                const id = b.batchId ?? b.id;
                const lvId = b.levelId ?? null;
                const lvName = b.levelName ?? '';
                const handleSessions = () => (onSessions ? onSessions(b) : defaultGotoSessions(b));

                const branchDisplay = b.branchName ?? b.branch?.name ?? '—';

                const sessionDaysNums = parseSessionDays(b.sessionDays ?? b.session_days ?? '');
                const startTod = b.defaultStartTod ?? b.startTod ?? b.startTime ?? '—';
                const endTod = b.defaultEndTod ?? b.endTod ?? b.endTime ?? '—';

                const chargesMode = b.chargesMode ?? b.charges_mode ?? 'PER_SESSION_HOURLY';
                const periodBilling = isPeriodBillingMode(chargesMode);
                const periodInterval = b.periodInterval ?? b.period_interval ?? 'MONTHLY';

                let chargeAmount = '';
                let chargeSubtitle = '';
                if (chargesMode === 'PER_SESSION' || chargesMode === 'PER_SESSION_HOURLY' || chargesMode === 'RECURRING_SESSIONS') {
                    const sessionCharge = b.sessionCharge ?? b.session_charge;
                    chargeAmount = sessionCharge ? formatCurrency(sessionCharge) : '';
                } else if (periodBilling) {
                    const periodCharge = b.periodCharge ?? b.period_charge;
                    chargeAmount = periodCharge ? formatCurrency(periodCharge) : '';
                    chargeSubtitle = formatPeriodInterval(periodInterval);
                }

                const cfMode = b.carryForwardMode ?? b.carry_forward_mode ?? b.carryForwardAllowed ?? b.carry_forward_allowed;
                const showCarryForwardBadge = !periodBilling && (cfMode === 'ALLOWED' || cfMode === true || cfMode === 'ENROLLMENT_LEVEL');

                return (
                    <div key={id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="table-header px-6 py-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            {t('batches.batchNumber', { id })}
                                        </span>
                                        {showCarryForwardBadge && (cfMode === 'ALLOWED' || cfMode === true) && (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 cursor-help"
                                                title={batchPolicyTitle('ALLOWED')}
                                            >
                                                {t('batches.carryForward')}
                                            </span>
                                        )}
                                        {showCarryForwardBadge && cfMode === 'ENROLLMENT_LEVEL' && (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 cursor-help"
                                                title={batchPolicyTitle('ENROLLMENT_LEVEL')}
                                            >
                                                {t('batches.perStudent')}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 break-words">
                                        {b.name ?? '—'}
                                    </h3>
                                </div>
                                <div className="flex flex-wrap gap-2 ml-4 flex-shrink-0">
                                    {onEdit && (
                                        <button type="button" onClick={() => onEdit(b)} className="btn-action-edit" title={t('batches.editTitle')}>
                                            {t('common.edit')}
                                        </button>
                                    )}
                                    <button type="button" onClick={handleSessions} className="btn-action-view" title={t('batches.sessionsTitle')}>
                                        {t('nav.sessions')}
                                    </button>
                                    {onArchive && (
                                        <button type="button" onClick={() => onArchive(b)} className="btn-action-archive" title={t('batches.archiveTitle')}>
                                            {t('common.archive')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('batches.level')}</div>
                                        <div className="text-sm font-medium text-gray-900">{lvName || '—'}</div>
                                        {lvId && <div className="text-xs text-gray-500 mt-0.5">{t('common.id')} {lvId}</div>}
                                    </div>
                                    <div>
                                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('batches.branch')}</div>
                                        <div className="text-sm font-medium text-gray-900">{branchDisplay}</div>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('batches.schedule')}</div>
                                    <div className="space-y-2">
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">{t('batches.sessionDays')}</div>
                                            <div className="flex flex-wrap gap-1">
                                                {sessionDaysNums.length > 0 ? sessionDaysNums.map((dayNum) => (
                                                    <span key={dayNum} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                                                        {t(`days.short.${dayNum}`, { defaultValue: String(dayNum) })}
                                                    </span>
                                                )) : <span className="text-sm text-gray-400">—</span>}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-gray-500 mb-1">{t('batches.sessionTime')}</div>
                                            <div className="text-sm font-medium text-gray-900">{startTod} - {endTod}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{t('batches.charges')}</div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">
                                            {formatChargesMode(chargesMode)}
                                        </div>
                                        {chargeSubtitle && (
                                            <div className="text-xs text-gray-600 mt-0.5">{chargeSubtitle}</div>
                                        )}
                                        {chargeAmount && (
                                            <div className="text-lg font-bold text-purple-600 mt-1">{chargeAmount}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default BatchTable;
